import { useState } from "react";
import { Copy, HelpCircle, Image as ImageIcon, MousePointerClick, Type } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Plantilla Ventura HR:
 * - Cabecera = imagen (logo)
 * - Cuerpo = texto con *negrita*
 * - Botones: Registrar jornada / Modificar jornada
 *
 * Variables: {{1}} nombre, {{2}} fecha, {{3}} horario.
 * Token en botones URL.
 */
export const CUERPO_PLANTILLA_RICH =
  "*Hola, {{1}}.*\n\n" +
  "Registra tu jornada del *{{2}}* (horario previsto *{{3}}*).\n\n" +
  "Puedes *registrar* la entrada o salida que corresponda, o *modificar* la hora de forma manual.\n\n" +
  "👇 Acceso Ventura HR 👇";

export const PLANTILLA_FALLBACK_TEXTO =
  "Hola {{1}},\n\n" +
  "Registra tu jornada del {{2}} ({{3}}).\n" +
  "Enlace: {{4}}\n\n" +
  "Ventura HR";

function copiar(texto, etiqueta) {
  navigator.clipboard.writeText(texto).then(
    () => toast.success(`${etiqueta} copiado`),
    () => toast.error("No se pudo copiar")
  );
}

export default function GuiaPlantillasFichajeWhatsApp({ triggerClassName = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5 border-sky-200 text-sky-800 ${triggerClassName}`}
        >
          <HelpCircle className="h-4 w-4" />
          Guía de plantillas
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Plantilla WhatsApp — Ventura HR</DialogTitle>
          <DialogDescription>
            Logo y botones se configuran en Altiria/Meta. Ventura solo rellena las variables y el
            token del enlace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-zinc-700">
          <div className="rounded-lg border border-violet-100 bg-violet-50/70 px-3 py-2.5 text-xs text-violet-950 space-y-1.5">
            <p className="font-medium">Acciones de la mini-web</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                <strong>Registrar jornada</strong> — entrada o salida según lo pendiente ese día,
                a la hora prevista. El mismo enlace sirve para ambas. El aviso se dispara minutos
                después de la hora prevista de ese picado.
              </li>
              <li>
                <strong>Modificar jornada</strong> — picado manual: el trabajador elige la hora.
              </li>
              <li>
                Tras cada acción se muestra un <strong>mensaje de confirmación</strong>.
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
              <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              <div className="text-xs space-y-1">
                <p className="font-medium text-sky-950">1. Cabecera → Imagen (logo Ventura HR)</p>
                <p className="text-zinc-600">
                  Sube el logo corporativo en Altiria/Meta (cabecera IMAGE). Ahí van marca y colores.
                </p>
              </div>
            </div>

            <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
              <Type className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              <div className="text-xs space-y-2 w-full">
                <p className="font-medium text-sky-950">2. Cuerpo — 3 variables</p>
                <p className="text-zinc-600">
                  <strong>{"{{1}}"} nombre</strong>, <strong>{"{{2}}"} fecha</strong>,{" "}
                  <strong>{"{{3}}"} horario</strong> (ej. 08:00 a 15:00).
                </p>
                <pre className="whitespace-pre-wrap rounded-md bg-white p-3 text-[11px] text-zinc-800 border border-sky-100 font-mono">
                  {CUERPO_PLANTILLA_RICH}
                </pre>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1 border-sky-200"
                  onClick={() => copiar(CUERPO_PLANTILLA_RICH, "Cuerpo de plantilla")}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copiar cuerpo
                </Button>
              </div>
            </div>

            <div className="flex gap-2 rounded-lg border border-sky-100 p-3">
              <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              <div className="text-xs space-y-1.5">
                <p className="font-medium text-sky-950">3. Botones URL</p>
                <ul className="list-disc pl-4 text-zinc-600 space-y-1">
                  <li>
                    <strong>Registrar jornada</strong>
                    <br />
                    Base:{" "}
                    <code className="rounded bg-zinc-100 px-1 break-all">
                      https://TU-DOMINIO/wa/jornada/
                    </code>{" "}
                    + token
                  </li>
                  <li>
                    <strong>Modificar jornada</strong>
                    <br />
                    Misma base +{" "}
                    <code className="rounded bg-zinc-100 px-1">token?accion=modificar</code>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-xs text-amber-950 space-y-1">
            <p className="font-medium">Fallback sin botones (4 variables)</p>
            <pre className="mt-1 whitespace-pre-wrap rounded bg-white p-2 font-mono text-[11px] border border-amber-100">
              {PLANTILLA_FALLBACK_TEXTO}
            </pre>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-1 gap-1"
              onClick={() => copiar(PLANTILLA_FALLBACK_TEXTO, "Plantilla simple")}
            >
              <Copy className="h-3.5 w-3.5" />
              Copiar fallback
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
